import { collectPageBlockTargets } from '../siteLinkTargets';

/**
 * De quel bloc parle un problème, et vers lequel naviguer.
 *
 * Un lien cassé vit souvent dans un bloc **imbriqué** — un bouton posé dans une
 * colonne de section, un CTA dans un onglet. Ce bloc-là n'a ni titre lisible ni
 * élément propre dans le canvas : c'est son bloc racine qu'il faut atteindre.
 * Les clés de `zones` étant `<idDuParent>:<nomDeZone>`, la filiation se lit
 * directement dans les données, sans rien ajouter aux blocs.
 */

const idOf = (block) => String(block?.props?.id ?? '');

/** Index enfant → parent, déduit des clés de zone. */
export function buildParentIndex(puckData) {
  const parents = new Map();
  const zones = (puckData?.zones && typeof puckData.zones === 'object') ? puckData.zones : {};
  Object.entries(zones).forEach(([key, blocks]) => {
    const parentId = String(key).split(':')[0];
    if (!parentId) return;
    (blocks || []).forEach((block) => {
      const id = idOf(block);
      if (id) parents.set(id, parentId);
    });
  });
  return parents;
}

/**
 * Le bloc racine dont dépend un bloc. La remontée est plafonnée au nombre de
 * liens connus : des données abîmées (une zone qui se référence elle-même) ne
 * doivent pas figer l'éditeur.
 */
export function rootBlockIdOf(blockId, parents) {
  let current = blockId;
  for (let hops = 0; hops <= parents.size; hops += 1) {
    const parent = parents.get(current);
    if (!parent || parent === current) return current;
    current = parent;
  }
  return current;
}

/**
 * Tous les blocs d'une portée — racine et imbriqués — avec de quoi les nommer.
 *
 * Le libellé d'un bloc racine vient de `collectPageBlockTargets` : le titre que
 * l'utilisateur lit sur la page, à défaut son ancre, à défaut son type. Un bloc
 * imbriqué reprend celui de son parent, suffixé de son propre type — c'est ce
 * qui rend une ligne du panneau reconnaissable dans une page longue.
 *
 * @returns {Map<string, { block, rootBlockId, label, type }>}
 */
export function buildBlockIndex(scope, labelForType) {
  const puckData = scope?.puckData;
  const parents = buildParentIndex(puckData);
  const rootLabels = new Map(
    collectPageBlockTargets({ puckData }, labelForType)
      .map((target) => [target.blockId, target.label]),
  );

  const index = new Map();
  const add = (block) => {
    const id = idOf(block);
    if (!id) return;
    const rootBlockId = rootBlockIdOf(id, parents);
    const rootLabel = rootLabels.get(rootBlockId) || '';
    const type = String(block?.type || '');
    const ownLabel = labelForType ? labelForType(type) : type;
    index.set(id, {
      block,
      rootBlockId,
      type,
      label: id === rootBlockId
        ? (rootLabel || ownLabel)
        : [rootLabel, ownLabel].filter(Boolean).join(' › '),
    });
  };

  (puckData?.content || []).forEach(add);
  Object.values((puckData?.zones && typeof puckData.zones === 'object') ? puckData.zones : {})
    .forEach((blocks) => (blocks || []).forEach(add));
  return index;
}
